import { Star, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { ReviewsData } from "../types/PerformanceTypes";
import { STAR_COLORS, C } from "../../../constants/constants";
import { starToInt } from "../utils/PerformanceUtils";
import Card from "../ui/Card";
import SectionTitle from "../ui/SectionTitle";
import StarsDisplay from "../ui/StarsDisplay";

 
const DeltaBadge = ({ current, previous }: { current: number; previous: number }) => {
  if (!previous) return null;
  const pct = Math.round(((current - previous) / previous) * 100);
  const up  = pct > 0;
  const eq  = pct === 0;
  if (eq) return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-400">
      <Minus size={9} /> 0%
    </span>
  );
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold
      ${up ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
      {up ? <TrendingUp size={9} strokeWidth={2.5} /> : <TrendingDown size={9} strokeWidth={2.5} />}
      {up ? "+" : ""}{pct}%
    </span>
  );
};

 
interface ReviewsTabProps {
  data:                ReviewsData;
  compareEnabled?:     boolean;
  compareData?:        ReviewsData;
  primaryPeriodLabel?:  string;
  comparePeriodLabel?:  string;
}

 
const ReviewsTab = ({
  data,
  compareEnabled,
  compareData,
  primaryPeriodLabel  = "Période A",
  comparePeriodLabel  = "Période B",
}: ReviewsTabProps) => {
  const { averageRating, totalCount, distribution, reviews } = data;
  const distData = [5, 4, 3, 2, 1].map((star) => ({ star, count: distribution[star] || 0 }));
  const maxCount = Math.max(...distData.map((d) => d.count), 1);

   const cmpDistData = compareEnabled && compareData
    ? [5, 4, 3, 2, 1].map((star) => ({ star, count: compareData.distribution[star] || 0 }))
    : null;
  const cmpMaxCount = cmpDistData ? Math.max(...cmpDistData.map((d) => d.count), 1) : 1;
  const globalMax   = Math.max(maxCount, cmpMaxCount);

  return (
    <div className="flex flex-col gap-5 animate-[gbpFadeIn_0.2s_ease]">

      <div className="flex flex-wrap gap-4">

        
         <Card className={`flex flex-col items-center justify-center gap-2.5 py-7 px-6
          ${compareEnabled ? "flex-[0_0_160px]" : "flex-[0_0_180px]"}`}>
          {compareEnabled && (
            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wide mb-0.5">
              {primaryPeriodLabel}
            </span>
          )}
          <span className="text-[52px] font-extrabold text-slate-900 leading-none tracking-[-0.04em]">
            {averageRating?.toFixed(1) ?? "—"}
          </span>
          <StarsDisplay n={Math.round(averageRating || 0)} />
          <span className="text-xs text-slate-400 font-medium">{totalCount} avis</span>
        </Card>

        
         {compareEnabled && compareData && (
          <Card className="flex-[0_0_160px] flex flex-col items-center justify-center gap-2.5 py-7 px-6
                           border-violet-200 bg-violet-50/40">
            <span className="text-[10px] font-bold text-violet-500 uppercase tracking-wide mb-0.5">
              {comparePeriodLabel}
            </span>
            <span className="text-[52px] font-extrabold text-violet-700 leading-none tracking-[-0.04em]">
              {compareData.averageRating?.toFixed(1) ?? "—"}
            </span>
            <StarsDisplay n={Math.round(compareData.averageRating || 0)} />
            <span className="text-xs text-violet-400 font-medium">{compareData.totalCount} avis</span>

            
             {averageRating != null && compareData.averageRating != null && (
              <div className="mt-1">
                <DeltaBadge
                  current={averageRating * 10}
                  previous={compareData.averageRating * 10}
                />
              </div>
            )}
          </Card>
        )}

        
         <Card className="flex-[1_1_280px]">
          <SectionTitle>Distribution des notes</SectionTitle>
          <div className="flex flex-col gap-2.5 mt-2">
            {distData.map(({ star, count }, idx) => {
              const cmpCount = cmpDistData?.[idx]?.count ?? 0;
              return (
                <div key={star} className="flex items-center gap-2.5">
                  <span className="w-5 text-right text-xs font-semibold text-slate-500 flex-shrink-0">
                    {star}
                  </span>
                  <Star size={11} fill={STAR_COLORS[star - 1]} stroke="none" className="flex-shrink-0" />

                  
                   <div className="flex-1 relative">
                    
                     {compareEnabled && cmpDistData && (
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-[3px]">
                        <div
                          className="h-full rounded-full opacity-35 transition-[width] duration-500 ease-out"
                          style={{ width: `${(cmpCount / globalMax) * 100}%`, background: STAR_COLORS[star - 1] }}
                        />
                      </div>
                    )}
                    
                     <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-[width] duration-500 ease-out"
                        style={{ width: `${(count / globalMax) * 100}%`, background: STAR_COLORS[star - 1] }}
                      />
                    </div>
                  </div>

                  
                   <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="w-7 text-right text-xs font-semibold text-gray-700 font-mono">
                      {count}
                    </span>
                    {compareEnabled && cmpDistData && (
                      <span className="text-[10px] text-slate-400">
                        / {cmpCount}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          
           {compareEnabled && cmpDistData && (
            <div className="flex items-center gap-4 mt-3 pt-2.5 border-t border-slate-100">
              <span className="flex items-center gap-1.5 text-[11px] text-blue-600 font-semibold">
                <span className="w-8 h-2 rounded-full bg-amber-400 opacity-90 inline-block" />
                {primaryPeriodLabel}
              </span>
              <span className="flex items-center gap-1.5 text-[11px] text-violet-500 font-semibold">
                <span className="w-8 h-2 rounded-full bg-amber-400 opacity-35 inline-block" />
                {comparePeriodLabel}
              </span>
            </div>
          )}
        </Card>
      </div>

      
       <Card>
        <SectionTitle>Avis récents</SectionTitle>

        {reviews.length === 0 ? (
          <p className="text-slate-400 text-[13px]">Aucun avis disponible.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {reviews.slice(0, 10).map((r, i) => {
              const stars = starToInt(r.starRating);
              return (
                <div
                  key={r.reviewId || i}
                  className="px-[18px] py-4 bg-slate-50 rounded-[10px]
                             border-l-[3px] shadow-sm
                             hover:shadow-md transition-shadow duration-200"
                  style={{ borderLeftColor: STAR_COLORS[stars - 1] || "#e2e8f0" }}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      {r.reviewer?.profilePhotoUrl ? (
                        <img src={r.reviewer.profilePhotoUrl} alt=""
                             className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                             style={{ background: `${C.primary}18` }}>
                          <span className="text-[13px] font-bold" style={{ color: C.primary }}>
                            {(r.reviewer?.displayName || "A")[0].toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div>
                        <p className="text-[13px] font-semibold text-slate-900 m-0">
                          {r.reviewer?.displayName || "Anonyme"}
                        </p>
                        <p className="text-[11px] text-slate-400 mt-0.5 m-0">
                          {r.createTime
                            ? new Date(r.createTime).toLocaleDateString("fr-FR", {
                                day: "numeric", month: "short", year: "numeric",
                              })
                            : ""}
                        </p>
                      </div>
                    </div>
                    <StarsDisplay n={stars} />
                  </div>

                  {r.comment && (
                    <p className="mt-2.5 mb-0 text-[13px] text-slate-600 leading-[1.65]">
                      {r.comment}
                    </p>
                  )}

                  {r.reviewReply?.comment && (
                    <div className="mt-2.5 px-3.5 py-2.5 rounded-lg border-l-2 bg-blue-50"
                         style={{ borderLeftColor: C.primary }}>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.06em] mb-1 m-0"
                         style={{ color: C.primary }}>
                        Réponse du propriétaire
                      </p>
                      <p className="m-0 text-xs text-slate-600 leading-[1.6]">
                        {r.reviewReply.comment}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
};

export default ReviewsTab;